import React from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Target, Heart, Zap, Users, Globe, ArrowRight } from 'lucide-react';
import PageLayout, { PageHero } from './layout/PageLayout';
import SEO from './SEO';

const AboutPage = ({ onBack, onLogin, onRegister }) => {
  const values = [
    {
      icon: Target,
      title: 'Mission-Driven',
      description: 'We believe testing should accelerate development, not slow it down. Our mission is to make quality assurance easier to manage.',
      gradient: 'from-purple-500 to-blue-500'
    },
    {
      icon: Heart,
      title: 'QA-First',
      description: 'Built by QA professionals, with experience in both manual testing and automation.',
      gradient: 'from-pink-500 to-rose-500'
    },
    {
      icon: Zap,
      title: 'Innovation',
      description: 'We use AI to assist test creation, with review and clear results as part of the workflow.',
      gradient: 'from-amber-500 to-orange-500'
    },
    {
      icon: Users,
      title: 'Community',
      description: 'We grow together with our users. Your feedback shapes our roadmap, and your success is our success.',
      gradient: 'from-green-500 to-emerald-500'
    }
  ];

  return (
    <PageLayout onBack={onBack} onLogin={onLogin} onRegister={onRegister}>
      <SEO 
        title="About Us - Lexa.run | AI-Powered Test Automation"
        description="Meet the QA experience behind Lexa.run and learn how we approach building test automation tools."
      />

      <PageHero
        badge={<><Globe className="w-4 h-4" /> Our Story</>}
        title="Built from experience."
        titleHighlight="Focused on better testing."
        subtitle="Two QA professionals. Twenty years of experience each. A shared goal of making testing easier to manage."
      />

      {/* Story Section */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              Lexa is built by two QA professionals with 20 years of experience each,
              bringing together manual testing and test automation.
            </p>
            <p className="text-gray-600 leading-relaxed mb-6">
              That experience shapes what we build: tools for creating tests, running
              them, and understanding results in one workspace. AI can help with test
              creation, while people remain responsible for reviewing coverage and results.
            </p>
            <p className="text-gray-600 leading-relaxed">
              We are at an early stage. We are building Lexa around practical testing
              workflows and welcome feedback from the teams exploring it. Tell
              us what would make the product useful for your organization.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4 text-purple-600 border-purple-200">
              <Heart className="w-4 h-4 mr-1" /> Our Values
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              What drives us forward
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              These principles guide every decision we make
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, idx) => (
              <Card key={idx} className="p-8 rounded-3xl border border-gray-200 hover:shadow-xl transition-all duration-300">
                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${value.gradient} text-white mb-6`}>
                  <value.icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%23ffffff%22 fill-opacity=%220.05%22%3E%3Cpath d=%22M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-50" />
        
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Join us on our mission
          </h2>
          <p className="text-xl text-purple-100 mb-10">
            Ready to transform your testing workflow?
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={onRegister}
              size="lg"
              className="rounded-full bg-white text-purple-600 hover:bg-gray-100 px-10 py-6 text-lg font-semibold shadow-xl"
            >
              Get Started Free
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              onClick={() => {
                window.history.pushState(null, '', '/contact');
                window.dispatchEvent(new PopStateEvent('popstate'));
              }}
              size="lg"
              variant="outline"
              className="rounded-full border-2 border-white text-white hover:bg-white/10 px-10 py-6 text-lg font-semibold"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default AboutPage;
